import React, { lazy, Suspense, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { useSelector } from "redux/typedUseSelector";
import {
  Route,
  Redirect,
  Switch,
  useHistory,
  useLocation,
} from "react-router-dom";

import authActions from "redux/ducks/auth/authActions";
import profileActions from "redux/ducks/profile/profileActions";
import messagesActions from "redux/ducks/messages/messagesActions";

import { mobileHeight } from "utils/helpers";
import { FLAG, isFlagOn } from "utils/verificationFlags";
import GlassPopup from "components/GlassPopup/GlassPopup";
import FemaleVerifyModal from "components/FemaleVerifyModal/FemaleVerifyModal";
import verifBadge from "images/Icons/verif-new.png";
import "./App.scss";

// Экраны флоу жест-верификации: пока действует отказ (статус 1), разрешены
// только они (экран отказа → съёмка → «на проверке»), остальное уводит на отказ.
const GESTURE_FLOW_PATHS = ["/fill/verifyerror", "/fill/verifyphoto", "/fill/verifyreview"];

const Auth = lazy(() => import("./pages/Auth/Auth"))
const AuthLegalPage = lazy(() => import("./pages/Auth/pages/AuthLegalPage/AuthLegalPage"))
const Help = lazy(() => import("./pages/Auth/pages/Help/Help"))
const Contacts = lazy(() => import("./pages/Auth/pages/Contacts/Contacts"))
const FinalFill = lazy(() => import("./pages/FillProfile/components/FinalFill"))
const AppLayout = lazy(() => import("./pages/AppLayout/AppLayout"))
const Blocked = lazy(() => import("./pages/System/Blocked/Blocked"))
const Recovery = lazy(() => import("./pages/System/Recovery/Recovery"))
const FillProfile = lazy(() => import("./pages/FillProfile/FillProfile"))
const VerifyReview = lazy(() => import("./pages/Verification/VerifyReview"))
const VerifyError = lazy(() => import("./pages/Verification/VerifyError"))
const PhotoRejected = lazy(() => import("./pages/Verification/PhotoRejected"))
const Landing = lazy(() => import("./pages/Landing/Landing"))
const Plain404 = lazy(() => import("./pages/System/Plain404"))

function App() {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const {
    isAuth,
    systemBan,
    userDeleted,
    isFilling,
    tokenIsValid,
  } = useSelector((state) => state.authReducer);

  const {
    isBlockedWithouVerification,
    isBlockedWithoutAnyPhoto,
    gestureVerifyStatus,
    photoRejected,
    mainPhotoRejected,
    verifiedSuccessModal,
    profileLoading,
  } = useSelector((state) => state.profileReducer);
  const { t } = useTranslation();

  // Отказ жест-верификации: статус 1 из extended-профиля, а до его ответа —
  // липкий флаг (гейт срабатывает сразу на старте, потом сверяется с бэком).
  const gestureRejected = !!isAuth && (
    gestureVerifyStatus === 1
    || (gestureVerifyStatus === null && isFlagOn(FLAG.verificationRejected))
  );
  // Отказ модерации главного фото: из getProfileInfo (главное с verified 1 /
  // lastPhotoRejection без годного фото) ИЛИ из extended (mainPhotoVerifiedStatus 1
  // при настоящем фото); до загрузки профиля — липкий флаг. Отказ жеста
  // приоритетнее — его не перебиваем.
  const anyPhotoRejected = !!isAuth && !gestureRejected && (
    photoRejected
    || mainPhotoRejected
    || (profileLoading && isFlagOn(FLAG.photoRejected))
  );

  useEffect(() => {
    // Ops Google login stores fingerprint as opsFingerPrint — reuse for SPA refresh.
    try {
      if (!window.localStorage.fingerPrint && window.localStorage.opsFingerPrint) {
        window.localStorage.fingerPrint = window.localStorage.opsFingerPrint;
      }
    } catch (_) {}
  }, []);

  useEffect(() => {
    isAuth && dispatch(messagesActions.getFolders())
  }, [dispatch, isAuth]);

  useEffect(() => {
    // DEV-autologin: не дёргаем refresh на старте — он затирал бы вшитый токен,
    // если refresh-токен уже использован на другом устройстве (мак).
    const devAutoLogin =
      process.env.REACT_APP_ENV_MODE === "development" &&
      process.env.REACT_APP_DEV_AUTOLOGIN !== "0" &&
      !(window as any).__DEV_NO_AUTOLOGIN__ &&
      !!process.env.REACT_APP_DEV_ACCESS_TOKEN;
    if (!devAutoLogin && window.localStorage.idProfile && window.localStorage.refreshToken) {
      dispatch(authActions.refresh("app extendedProfile"));
    }
    isAuth && dispatch(profileActions.getExtendedProfileInfo());
    isAuth && dispatch(profileActions.getProfileInfo());
  }, [tokenIsValid, isAuth, dispatch]);

  useEffect(() => {
    userDeleted && history.push("/recovery");
    systemBan && history.push("/blocked");
  }, [userDeleted, systemBan, history]);

  useEffect(() => {
    mobileHeight();
  }, []);

  useEffect(() => {
    // Гейты отказа (фото/жест) приоритетнее: не уводим на загрузку/съёмку,
    // их экраны покажут гейты ниже.
    if (anyPhotoRejected || gestureRejected) { return; }
    // Селфи уже отправлено и ждёт проверки (статус 2 / pending-флаг): бэк ещё
    // может отвечать notVerified, но это НЕ повод гнать на повторную
    // верификацию (как в приле) — вместо экрана съёмки попап «дождитесь».
    const verificationPending = gestureVerifyStatus === 2 || isFlagOn(FLAG.verificationPending);
    if (isAuth && isBlockedWithouVerification && verificationPending) {
      dispatch(profileActions.setNotVerified(false));
      dispatch(profileActions.setFemaleVerifyModal("pending"));
    } else {
      isAuth &&
        isBlockedWithouVerification &&
        dispatch(authActions.setIsFilling(true));
      isAuth && isBlockedWithouVerification && history.push("/fill/verifyphoto");
    }
    isAuth &&
      isBlockedWithoutAnyPhoto &&
      dispatch(authActions.setIsFilling(true));
    isAuth && isBlockedWithoutAnyPhoto && history.push("/fill/loadphoto");
  }, [isBlockedWithouVerification, isBlockedWithoutAnyPhoto, isAuth, dispatch, history, anyPhotoRejected, gestureRejected, gestureVerifyStatus]);

  // Гейт отказа жест-верификации 1в1 с приложением (core/profile/sagas):
  // статус 1 (= однозначно ОТКАЗ; во время проверки бэк отдаёт 2) →
  // незакрываемый экран отказа. replace (не push) + зависимость от
  // location.pathname: любой уход тут же возвращает обратно. Разрешены только
  // экраны самого флоу (отказ → съёмка → «на проверке»), иначе повторная
  // попытка зацикливалась бы обратно на отказ.
  // Статус 2 (на проверке) НЕ гейтим — как в приле: экран «на проверке»
  // показывается только сразу после отправки, дальше — баннер в профиле
  // (раньше гейт по 2 запирал юзера на этом экране: «Понятно» → обратно).
  useEffect(() => {
    if (!isAuth) { return; }
    const path = location.pathname;
    if (gestureRejected) {
      if (!GESTURE_FLOW_PATHS.includes(path)) {
        history.replace("/fill/verifyerror");
      }
    } else if ((gestureVerifyStatus === 0 || gestureVerifyStatus === 2) && path === "/fill/verifyerror") {
      // Отказ снят (одобрили / уже на проверке) — экран отказа неактуален.
      history.replace("/profile");
    }
  }, [gestureRejected, gestureVerifyStatus, isAuth, history, location.pathname]);

  // Гейт модерации фото 1в1 с приложением (core/profile/sagas): фото отклонено
  // → незакрываемый экран повторной модерации.
  // Периодически перепроверяем статус профиля (модерация фото/верификация),
  // чтобы гейт срабатывал БЕЗ перезагрузки страницы: если фото отклонят в
  // реальном времени — экран модерации появится сам. Плюс перепроверка при
  // возврате на вкладку.
  useEffect(() => {
    if (!isAuth) { return undefined; }
    const poll = () => {
      dispatch(profileActions.getProfileInfo());
      dispatch(profileActions.getExtendedProfileInfo(true)); // тихо, без скелетонов
    };
    const id = window.setInterval(poll, 20000);
    const onFocus = () => poll();
    window.addEventListener("focus", onFocus);
    return () => {
      window.clearInterval(id);
      window.removeEventListener("focus", onFocus);
    };
  }, [isAuth, dispatch]);

  useEffect(() => {
    if (!isAuth) { return; }
    const path = location.pathname;
    if (anyPhotoRejected && path !== "/fill/photorejected") {
      // replace (не push) и зависимость от location.pathname: любой уход с
      // гейта (в т.ч. свайп «назад» на iOS) тут же возвращает обратно, пока
      // фото отклонено — без годного фото в веб не проскочить.
      history.replace("/fill/photorejected");
    } else if (!anyPhotoRejected && !gestureRejected && !profileLoading && path === "/fill/photorejected") {
      // Статус модерации снялся (фото принято/на проверке) — уходим с гейта.
      // Дальше решают остальные гейты: нет фото → loadphoto, есть → поиск.
      history.replace("/");
    }
  }, [anyPhotoRejected, gestureRejected, profileLoading, isAuth, location.pathname, history]);

  const closeVerifiedSuccess = () => {
    dispatch(profileActions.setVerifiedSuccessModal(false));
  };

  return (
    <>
      {/* «Профиль верифицирован» (как PURCHASE_STATUS в приле) — один раз после
          одобрения, если ждали проверку (pending). */}
      <GlassPopup
        visible={!!isAuth && verifiedSuccessModal}
        centered
        showClose
        image={verifBadge}
        title={t("auth.verifiedSuccessTitle")}
        description={t("auth.verifiedSuccessText")}
        primaryText={t("purchases.great")}
        primaryBlue
        onPrimary={closeVerifiedSuccess}
        onClose={closeVerifiedSuccess}
      />
      {isAuth && <FemaleVerifyModal />}
      <Suspense fallback={<div></div>}>
        <Switch>
          <Route path="/welcome" exact render={() => <Landing />} />
          <Route
            path="/auth/contacts"
            exact
            render={() => <Contacts />}
          />
          <Route
            path="/auth/help"
            exact
            render={() => <Help />}
          />
          <Route
            path="/auth/policy"
            exact
            render={() => <AuthLegalPage page="privacy" />}
          />
          <Route
            path="/auth/about"
            exact
            render={() => <AuthLegalPage page="about" />}
          />
          <Route
            path="/auth/security"
            exact
            render={() => <AuthLegalPage page="terms" />}
          />
          <Route
            path="/auth/guidelines"
            exact
            render={() => <AuthLegalPage page="rules" />}
          />
          <Route
            path="/fill/verifyreview"
            exact
            render={() => (isAuth ? <VerifyReview /> : <Plain404 />)}
          />
          <Route
            path="/fill/verifyerror"
            exact
            render={() => (isAuth ? <VerifyError /> : <Plain404 />)}
          />
          <Route
            path="/fill/photorejected"
            exact
            render={() => (isAuth ? <PhotoRejected /> : <Plain404 />)}
          />
          <Route
            path={["/fill", "/fill/:id"]}
            exact
            render={() => ((isAuth || isFilling) ? <FillProfile /> : <Plain404 />)}
          />
          <Route
            exact
            path={["/auth", "/auth/:id", "/auth/:id/:subId"]}
            render={() =>
              isAuth
                ? ((isFilling || isBlockedWithoutAnyPhoto)
                    ? <Auth />                 /* идёт регистрация/нет фото — шаги реги доступны (назад к городу), в поиск не пускаем */
                    : <Redirect to="/search" />)
                : <Auth />
            }
          />
          <Route
            path="/recovery"
            exact
            render={() => (userDeleted ? <Recovery /> : <Plain404 />)}
          />
          <Route
            path="/blocked"
            exact
            render={() => (systemBan ? <Blocked /> : <Plain404 />)}
          />
          <Route
            path="/fill/final"
            render={() => (isAuth ? <FinalFill /> : <Plain404 />)}
          />
          <Route
            path="/"
            render={() => {
              if (!isAuth) {
                return <Redirect to="/auth/phone" />;
              }
              if (isFilling || isBlockedWithoutAnyPhoto) {
                return <Redirect to="/fill/loadphoto" />;
              }
              return <AppLayout />;
            }}
          />
        </Switch>
      </Suspense>
    </>
  );
}

export default App;
